import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const PrivateChatSocketContext = createContext(null);

export const usePrivateChatSocket = () => {
  const context = useContext(PrivateChatSocketContext);
  if (!context) {
    throw new Error('usePrivateChatSocket must be used within a PrivateChatSocketProvider');
  }
  return context;
};

export const PrivateChatSocketProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const isUnmountedRef = useRef(false);
  const connectFnRef = useRef(null);
  
  // Message handlers that can be registered by components
  const messageHandlersRef = useRef(new Set());
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Store current values in refs
  const tokenRef = useRef(token);
  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  
  useEffect(() => {
    tokenRef.current = token;
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
  }, [token, user, isAuthenticated]);
  
  // Register a message handler
  const addMessageHandler = useCallback((handler) => {
    messageHandlersRef.current.add(handler);
    return () => {
      messageHandlersRef.current.delete(handler);
    };
  }, []);
  
  // Send message via WebSocket (not used for private chat - uses REST API)
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    logger.warn('⚠️ Private Chat WebSocket not connected, cannot send message');
    return false;
  }, []);
  
  // Connect function stored in ref to avoid dependency cycle
  useEffect(() => {
    connectFnRef.current = () => {
      if (!userRef.current?.user_id || !isAuthenticatedRef.current || isUnmountedRef.current) {
        return;
      }
      
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/chat`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          if (isUnmountedRef.current) {
            ws.close();
            return;
          }
          setConnected(true);
          reconnectAttempts.current = 0;
          
          // Authenticate immediately
          ws.send(JSON.stringify({
            type: 'auth',
            user_id: userRef.current.user_id,
            user_name: userRef.current.name
          }));
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Notify all registered handlers
            messageHandlersRef.current.forEach(handler => {
              try {
                handler(data);
              } catch (e) {
                logger.error('Error in private chat message handler:', e);
              }
            });
          } catch (e) {
            logger.error('Error parsing private chat message:', e);
          }
        };
        
        ws.onerror = (error) => {
          logger.error('❌ Private Chat WebSocket error:', error);
        };
        
        ws.onclose = (event) => {
          setConnected(false);
          wsRef.current = null;
          
          // Attempt reconnection if not unmounted and authenticated
          if (!isUnmountedRef.current && 
              isAuthenticatedRef.current && 
              reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            reconnectTimeoutRef.current = setTimeout(() => {
              if (!isUnmountedRef.current && connectFnRef.current) {
                connectFnRef.current();
              }
            }, delay);
          }
        };
      } catch (error) {
        logger.error('❌ Failed to create Private Chat WebSocket:', error);
      }
    };
  }, [API_URL]);
  
  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setConnected(false);
  }, []);
  
  // Reconnect function (for manual reconnection)
  const reconnect = useCallback(() => {
    if (connectFnRef.current) {
      reconnectAttempts.current = 0;
      connectFnRef.current();
    }
  }, []);
  
  // Connect when authenticated
  useEffect(() => {
    isUnmountedRef.current = false;
    
    if (isAuthenticated && user?.user_id && connectFnRef.current) {
      // Small delay to ensure context is fully ready
      const connectTimeout = setTimeout(() => {
        if (!isUnmountedRef.current && connectFnRef.current) {
          connectFnRef.current();
        }
      }, 100);
      
      return () => clearTimeout(connectTimeout);
    }
    
    return () => {
      isUnmountedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isAuthenticated, user?.user_id]);
  
  // Disconnect when user logs out - handle this through conditional connection
  // instead of setting state in effect
  useEffect(() => {
    if (!isAuthenticated || !user?.user_id) {
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Close WebSocket
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    }
  }, [isAuthenticated, user?.user_id]);
  
  const value = {
    connected,
    sendMessage,
    addMessageHandler,
    reconnect,
    disconnect
  };
  
  return (
    <PrivateChatSocketContext.Provider value={value}>
      {children}
    </PrivateChatSocketContext.Provider>
  );
};

export default PrivateChatSocketContext;
